'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import type { ReactNode } from 'react';
import { Suspense, useEffect, useState } from 'react';
import { Button } from '@/app/components/button';
import { Container } from '@/app/components/layouts/container';
import { MainMenuDesktop } from '@/app/components/layouts/top-navigation/main-menu/desktop';
import { PathWatcher } from '@/app/components/layouts/top-navigation/path-watcher';
import { TopNavigationLogo } from '@/app/components/layouts/top-navigation/shared/logo';
import { SubMenu } from '@/app/components/layouts/top-navigation/sub-menu';
import { preloadSubMenuLayouts } from '@/app/components/layouts/top-navigation/sub-menu/render-layout';
import { TOP_NAV_DATA_GA, TOP_NAV_DATA_TEST } from '@/app/components/layouts/top-navigation/tracking';
import { TOP_NAV_MENU_LABEL, TOP_NAV_Z_CLASSNAME } from '@/app/constants/top-navigation';
import { NAV_INTEREST } from '@/app/contents/top-navigation';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import type { NavItemType, TopNavigationThemeType, TopNavigationTransparentAppearanceType } from '@/app/types/top-navigation';
import type { VehicleType } from '@/app/types/vehicle-type';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { clearBodyLockScroll, enableBodyLockScroll } from '@/app/utils/lock-scroll';
const MainMenuMobile = dynamic(() => import('@/app/components/layouts/top-navigation/main-menu/mobile').then(mod => mod.MainMenuMobile), {
  ssr: false
});
const TopNavigationInnerStyled = cnva(['relative flex h-full items-center justify-between gap-x-4', TOP_NAV_Z_CLASSNAME], {
  variants: {
    theme: {
      light: 'text-gray-700',
      dark: 'text-white'
    }
  },
  defaultVariants: {
    theme: 'light'
  }
});
interface TopNavigationProps {
  logo: ReactNode;
  items: NavItemType[];
  vehicleType: VehicleType;
  theme?: TopNavigationThemeType;
  appearance?: TopNavigationTransparentAppearanceType;
  showRegister?: boolean;
}
export const TopNavigation = t0 => {
  const $ = _c(52);
  const {
    logo,
    items,
    vehicleType,
    theme,
    appearance: t1,
    showRegister: t2
  } = t0;
  const appearance = t1 === undefined ? "default" : t1;
  const showRegister = t2 === undefined ? true : t2;
  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const [shouldRenderMenu, setShouldRenderMenu] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(undefined);
  const isDesktopMenuOpen = !!isDesktop && isOpen;
  const adjustedTheme = isDesktopMenuOpen ? "light" : theme;
  let t3;
  if ($[0] !== isDesktop || $[1] !== isOpen) {
    t3 = () => {
      if (isOpen && !isDesktop) {
        return;
      }
      setShouldRenderMenu(true);
      setIsOpen(true);
      if (!isDesktop) {
        enableBodyLockScroll({
          onlyOverflow: true
        });
        setActiveItem(undefined);
      }
    };
    $[0] = isDesktop;
    $[1] = isOpen;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const openMenu = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsOpen(false);
      clearBodyLockScroll();
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const closeMenu = t4;
  let t5;
  let t6;
  if ($[4] !== isDesktop) {
    t5 = () => {
      if (isDesktop) {
        preloadSubMenuLayouts();
      }
    };
    t6 = [isDesktop];
    $[4] = isDesktop;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  if ($[7] !== isDesktopMenuOpen) {
    t7 = e => {
      if (!isDesktopMenuOpen || !e.relatedTarget) {
        return;
      }
      if (!e.relatedTarget.tagName) {
        closeMenu();
        return;
      }
      if (e.relatedTarget.tagName.toLowerCase() !== "aside") {
        closeMenu();
      }
    };
    $[7] = isDesktopMenuOpen;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const onMouseLeaveMenu = t7;
  let t8;
  if ($[9] !== isDesktop || $[10] !== isOpen) {
    t8 = <Suspense><PathWatcher isOpen={isOpen} isDesktop={isDesktop} setShouldRenderMenu={setShouldRenderMenu} closeMenu={closeMenu} setActiveItem={setActiveItem} /></Suspense>;
    $[9] = isDesktop;
    $[10] = isOpen;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== adjustedTheme) {
    t9 = TopNavigationInnerStyled({
      theme: adjustedTheme
    });
    $[12] = adjustedTheme;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = createDataAttributes({
      test: TOP_NAV_DATA_TEST.navigation
    });
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== appearance || $[16] !== isDesktopMenuOpen || $[17] !== logo) {
    t11 = !!logo && <TopNavigationLogo logo={logo} appearance={appearance} isDesktopMenuOpen={isDesktopMenuOpen} onMouseEnter={closeMenu} />;
    $[15] = appearance;
    $[16] = isDesktopMenuOpen;
    $[17] = logo;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== isDesktopMenuOpen || $[20] !== items || $[21] !== openMenu) {
    t12 = <MainMenuDesktop items={items} isOpen={isDesktopMenuOpen} onOpen={openMenu} onSetActiveItem={setActiveItem} onClose={closeMenu} />;
    $[19] = isDesktopMenuOpen;
    $[20] = items;
    $[21] = openMenu;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== showRegister) {
    t13 = !!showRegister && <Button href={NAV_INTEREST.href} aria-label={NAV_INTEREST.label} size="sm" width="auto" {...createDataAttributes({
      test: TOP_NAV_DATA_TEST.interestButton
    })} {...createDataGA({
      click: true,
      eventName: TOP_NAV_DATA_GA.topNavigation,
      text: NAV_INTEREST.label,
      url: NAV_INTEREST.href
    })} variant="primary" color="red" className="whitespace-nowrap" onFocus={closeMenu} onMouseEnter={closeMenu}>{NAV_INTEREST.label}</Button>;
    $[23] = showRegister;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const t14 = adjustedTheme === "dark" ? "white" : "black";
  let t15;
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = createDataAttributes({
      test: TOP_NAV_DATA_TEST.menuButton
    });
    t16 = createDataGA({
      click: true,
      eventName: TOP_NAV_DATA_GA.topNavigation,
      text: TOP_NAV_MENU_LABEL
    });
    $[25] = t15;
    $[26] = t16;
  } else {
    t15 = $[25];
    t16 = $[26];
  }
  let t17;
  if ($[27] !== openMenu || $[28] !== t14) {
    t17 = <Button size="sm" width="auto" variant="secondary" color={t14} onClick={openMenu} {...t15} {...t16} className="xl:hidden">{TOP_NAV_MENU_LABEL}</Button>;
    $[27] = openMenu;
    $[28] = t14;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== onMouseLeaveMenu || $[31] !== t12 || $[32] !== t13 || $[33] !== t17) {
    t18 = <div role="menubar" className="flex h-full items-center justify-end gap-x-4 xl:grow" onMouseLeave={onMouseLeaveMenu}>{t12}{t13}{t17}</div>;
    $[30] = onMouseLeaveMenu;
    $[31] = t12;
    $[32] = t13;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t11 || $[36] !== t18 || $[37] !== t9) {
    t19 = <Container aria-label="Navigation" as="nav" className={t9} {...t10}>{t11}{t18}</Container>;
    $[35] = t11;
    $[36] = t18;
    $[37] = t9;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== activeItem || $[40] !== isDesktop || $[41] !== isDesktopMenuOpen || $[42] !== isOpen || $[43] !== items || $[44] !== logo || $[45] !== shouldRenderMenu || $[46] !== vehicleType) {
    t20 = !!shouldRenderMenu && (isDesktop ? <SubMenu isOpen={isDesktopMenuOpen} activeItem={activeItem} isDesktop={true} onClose={closeMenu} /> : <MainMenuMobile logo={logo} isOpen={isOpen} activeItem={activeItem} items={items} vehicleType={vehicleType} onSetActiveItem={setActiveItem} onClose={closeMenu} />);
    $[39] = activeItem;
    $[40] = isDesktop;
    $[41] = isDesktopMenuOpen;
    $[42] = isOpen;
    $[43] = items;
    $[44] = logo;
    $[45] = shouldRenderMenu;
    $[46] = vehicleType;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t19 || $[49] !== t20 || $[50] !== t8) {
    t21 = <>{t8}{t19}{t20}</>;
    $[48] = t19;
    $[49] = t20;
    $[50] = t8;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  return t21;
};